/**
 * Editor state for the taxi waypoint editor. Holds an in-memory **draft** copy
 * of the active `TaxiPlan` that mutates live (so routing/animation reflect edits
 * immediately); the DB is the source of truth, flushed by an explicit Save in
 * the panel (hence no `persist` middleware, unlike `eventPlannerStore`).
 *
 * Mutations are immutable and set `dirty`. The panel reads `draftPlan`/`dirty`
 * and calls the `useTaxiPlanMutations` hook to persist, then `markSaved()`.
 */
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import {
  alwaysActive,
  makeId,
  mkRunway,
  type ClosureWindow,
  type NodeRole,
  type TaxiEdge,
  type TaxiGroup,
  type TaxiNode,
  type TaxiPlan,
  type TaxiRunway,
  type RunwayEnd,
} from '../data/taxiGraph'

export type EditorMode = 'view' | 'place_node' | 'place_edge' | 'drag' | 'close_segment' | 'place_runway'

interface State {
  /** is the editor surface mounted/active */
  editing: boolean
  draftPlan: TaxiPlan | null
  dirty: boolean
  mode: EditorMode
  placingRole: NodeRole
  activeGroupId: string | null
  selectedNodeId: string | null
  selectedEdgeId: string | null
  selectedRunwayId: string | null
  /** first endpoint while connecting an edge */
  edgeAnchorNodeId: string | null
  /** first threshold (scene feet) while drawing a runway */
  runwayAnchorPos: [number, number] | null

  setEditing(on: boolean): void
  loadPlan(plan: TaxiPlan): void
  /** Replace the draft with a freshly-built plan and mark it dirty (e.g. regenerate from geometry). */
  replaceDraft(plan: TaxiPlan): void
  setMode(mode: EditorMode): void
  setPlacingRole(role: NodeRole): void
  setActiveGroup(id: string | null): void

  selectNode(id: string | null): void
  selectEdge(id: string | null): void
  selectRunway(id: string | null): void
  clearSelection(): void

  addNode(pos: [number, number]): void
  moveNode(id: string, pos: [number, number]): void
  updateNode(id: string, patch: Partial<TaxiNode>): void

  /** Click a node in place_edge mode: set anchor, then create the edge on the 2nd. */
  clickNodeForEdge(nodeId: string): void
  addEdge(a: string, b: string): void
  updateEdge(id: string, patch: Partial<TaxiEdge>): void

  deleteSelected(): void

  addGroup(name: string): void
  updateGroup(id: string, patch: Partial<TaxiGroup>): void
  deleteGroup(id: string): void

  /** Real-time emergency close: flip an edge's master enabled flag. */
  toggleEdgeEnabled(id: string): void
  addClosure(edgeId: string, closure: Omit<ClosureWindow, 'id'>): void
  removeClosure(edgeId: string, closureId: string): void

  /** Click the map in place_runway mode: anchor the first threshold, then build on the 2nd. */
  clickForRunway(pos: [number, number]): void
  addRunway(a: [number, number], b: [number, number]): void
  updateRunway(id: string, patch: Partial<TaxiRunway>): void
  updateRunwayEnd(id: string, endIndex: 0 | 1, patch: Partial<RunwayEnd>): void
  moveRunwayEnd(id: string, endIndex: 0 | 1, threshold: [number, number]): void
  removeRunway(id: string): void
  /** Make this the single live runway (enable it, disable the others). */
  setActiveRunway(id: string): void

  markSaved(): void
  resetEditor(): void
}

/** Immutable plan update helper. */
function patchPlan(plan: TaxiPlan, mut: (p: TaxiPlan) => TaxiPlan): TaxiPlan {
  return mut({ ...plan })
}

export const useTaxiEditorStore = create<State>()(
  devtools(
    (set, get) => ({
      editing: false,
      draftPlan: null,
      dirty: false,
      mode: 'view',
      placingRole: 'taxi',
      activeGroupId: null,
      selectedNodeId: null,
      selectedEdgeId: null,
      selectedRunwayId: null,
      edgeAnchorNodeId: null,
      runwayAnchorPos: null,

      setEditing: (on) => set({ editing: on }, false, 'setEditing'),

      loadPlan: (plan) =>
        set(
          {
            draftPlan: plan,
            dirty: false,
            activeGroupId: plan.groups[0]?.id ?? null,
            selectedNodeId: null,
            selectedEdgeId: null,
            selectedRunwayId: null,
            edgeAnchorNodeId: null,
            runwayAnchorPos: null,
          },
          false,
          'loadPlan'
        ),

      replaceDraft: (plan) =>
        set(
          {
            draftPlan: plan,
            dirty: true,
            activeGroupId: plan.groups[0]?.id ?? null,
            selectedNodeId: null,
            selectedEdgeId: null,
            selectedRunwayId: null,
            edgeAnchorNodeId: null,
            runwayAnchorPos: null,
          },
          false,
          'replaceDraft'
        ),

      setMode: (mode) => set({ mode, edgeAnchorNodeId: null, runwayAnchorPos: null }, false, 'setMode'),
      setPlacingRole: (placingRole) => set({ placingRole }, false, 'setPlacingRole'),
      setActiveGroup: (activeGroupId) => set({ activeGroupId }, false, 'setActiveGroup'),

      selectNode: (id) => set({ selectedNodeId: id, selectedEdgeId: null, selectedRunwayId: null }, false, 'selectNode'),
      selectEdge: (id) => set({ selectedEdgeId: id, selectedNodeId: null, selectedRunwayId: null }, false, 'selectEdge'),
      selectRunway: (id) => set({ selectedRunwayId: id, selectedNodeId: null, selectedEdgeId: null }, false, 'selectRunway'),
      clearSelection: () =>
        set(
          { selectedNodeId: null, selectedEdgeId: null, selectedRunwayId: null, edgeAnchorNodeId: null, runwayAnchorPos: null },
          false,
          'clearSelection'
        ),

      addNode: (pos) => {
        const { draftPlan, placingRole, activeGroupId } = get()
        if (!draftPlan) return
        const groupId = activeGroupId ?? draftPlan.groups[0]?.id
        if (!groupId) return
        const node: TaxiNode = {
          id: makeId('n'),
          groupId,
          pos: [pos[0], pos[1]],
          role: placingRole,
          headingDeg: null,
          throughputPerSlot: placingRole === 'exit_to_taxiway' ? 4 : null,
          active: alwaysActive(),
          flags: {},
        }
        set(
          {
            draftPlan: { ...draftPlan, nodes: [...draftPlan.nodes, node] },
            dirty: true,
            selectedNodeId: node.id,
            selectedEdgeId: null,
          },
          false,
          'addNode'
        )
      },

      moveNode: (id, pos) => {
        const { draftPlan } = get()
        if (!draftPlan) return
        set(
          {
            draftPlan: {
              ...draftPlan,
              nodes: draftPlan.nodes.map((n) => (n.id === id ? { ...n, pos: [pos[0], pos[1]] } : n)),
            },
            dirty: true,
          },
          false,
          'moveNode'
        )
      },

      updateNode: (id, patch) => {
        const { draftPlan } = get()
        if (!draftPlan) return
        set(
          {
            draftPlan: {
              ...draftPlan,
              nodes: draftPlan.nodes.map((n) => (n.id === id ? { ...n, ...patch } : n)),
            },
            dirty: true,
          },
          false,
          'updateNode'
        )
      },

      clickNodeForEdge: (nodeId) => {
        const { edgeAnchorNodeId } = get()
        if (!edgeAnchorNodeId) {
          set({ edgeAnchorNodeId: nodeId }, false, 'edgeAnchor')
          return
        }
        if (edgeAnchorNodeId !== nodeId) get().addEdge(edgeAnchorNodeId, nodeId)
        set({ edgeAnchorNodeId: null }, false, 'edgeAnchorClear')
      },

      addEdge: (a, b) => {
        const { draftPlan, activeGroupId } = get()
        if (!draftPlan || a === b) return
        const exists = draftPlan.edges.some(
          (e) => (e.a === a && e.b === b) || (e.a === b && e.b === a)
        )
        if (exists) return
        const groupId = activeGroupId ?? draftPlan.groups[0]?.id
        if (!groupId) return
        const edge: TaxiEdge = {
          id: makeId('e'),
          groupId,
          a,
          b,
          role: 'taxiway',
          directed: false,
          weightMult: 1,
          active: alwaysActive(),
          closures: [],
          flags: {},
        }
        set(
          {
            draftPlan: { ...draftPlan, edges: [...draftPlan.edges, edge] },
            dirty: true,
            selectedEdgeId: edge.id,
            selectedNodeId: null,
          },
          false,
          'addEdge'
        )
      },

      updateEdge: (id, patch) => {
        const { draftPlan } = get()
        if (!draftPlan) return
        set(
          {
            draftPlan: {
              ...draftPlan,
              edges: draftPlan.edges.map((e) => (e.id === id ? { ...e, ...patch } : e)),
            },
            dirty: true,
          },
          false,
          'updateEdge'
        )
      },

      deleteSelected: () => {
        const { draftPlan, selectedNodeId, selectedEdgeId, selectedRunwayId } = get()
        if (!draftPlan) return
        if (selectedRunwayId) {
          get().removeRunway(selectedRunwayId)
          return
        }
        if (selectedNodeId) {
          set(
            {
              draftPlan: {
                ...draftPlan,
                nodes: draftPlan.nodes.filter((n) => n.id !== selectedNodeId),
                edges: draftPlan.edges.filter(
                  (e) => e.a !== selectedNodeId && e.b !== selectedNodeId
                ),
              },
              dirty: true,
              selectedNodeId: null,
            },
            false,
            'deleteNode'
          )
        } else if (selectedEdgeId) {
          set(
            {
              draftPlan: { ...draftPlan, edges: draftPlan.edges.filter((e) => e.id !== selectedEdgeId) },
              dirty: true,
              selectedEdgeId: null,
            },
            false,
            'deleteEdge'
          )
        }
      },

      addGroup: (name) => {
        const { draftPlan } = get()
        if (!draftPlan) return
        const group: TaxiGroup = {
          id: makeId('g'),
          name: name || 'New group',
          role: 'temporary',
          color: '#60a5fa',
          active: alwaysActive(),
          flags: {},
        }
        set(
          { draftPlan: { ...draftPlan, groups: [...draftPlan.groups, group] }, dirty: true, activeGroupId: group.id },
          false,
          'addGroup'
        )
      },

      updateGroup: (id, patch) => {
        const { draftPlan } = get()
        if (!draftPlan) return
        set(
          {
            draftPlan: {
              ...draftPlan,
              groups: draftPlan.groups.map((g) => (g.id === id ? { ...g, ...patch } : g)),
            },
            dirty: true,
          },
          false,
          'updateGroup'
        )
      },

      deleteGroup: (id) => {
        const { draftPlan, activeGroupId } = get()
        if (!draftPlan || draftPlan.groups.length <= 1) return // keep at least one
        const fallback = draftPlan.groups.find((g) => g.id !== id)!.id
        set(
          {
            draftPlan: {
              ...draftPlan,
              groups: draftPlan.groups.filter((g) => g.id !== id),
              // Reassign orphaned elements to the fallback group rather than deleting them.
              nodes: draftPlan.nodes.map((n) => (n.groupId === id ? { ...n, groupId: fallback } : n)),
              edges: draftPlan.edges.map((e) => (e.groupId === id ? { ...e, groupId: fallback } : e)),
            },
            dirty: true,
            activeGroupId: activeGroupId === id ? fallback : activeGroupId,
          },
          false,
          'deleteGroup'
        )
      },

      toggleEdgeEnabled: (id) => {
        const { draftPlan } = get()
        if (!draftPlan) return
        set(
          {
            draftPlan: patchPlan(draftPlan, (p) => ({
              ...p,
              edges: p.edges.map((e) =>
                e.id === id ? { ...e, active: { ...e.active, enabled: !e.active.enabled } } : e
              ),
            })),
            dirty: true,
          },
          false,
          'toggleEdgeEnabled'
        )
      },

      addClosure: (edgeId, closure) => {
        const { draftPlan } = get()
        if (!draftPlan) return
        const c: ClosureWindow = { ...closure, id: makeId('c') }
        set(
          {
            draftPlan: {
              ...draftPlan,
              edges: draftPlan.edges.map((e) =>
                e.id === edgeId ? { ...e, closures: [...e.closures, c] } : e
              ),
            },
            dirty: true,
          },
          false,
          'addClosure'
        )
      },

      removeClosure: (edgeId, closureId) => {
        const { draftPlan } = get()
        if (!draftPlan) return
        set(
          {
            draftPlan: {
              ...draftPlan,
              edges: draftPlan.edges.map((e) =>
                e.id === edgeId ? { ...e, closures: e.closures.filter((c) => c.id !== closureId) } : e
              ),
            },
            dirty: true,
          },
          false,
          'removeClosure'
        )
      },

      clickForRunway: (pos) => {
        const { runwayAnchorPos } = get()
        if (!runwayAnchorPos) {
          set({ runwayAnchorPos: pos }, false, 'runwayAnchor')
          return
        }
        get().addRunway(runwayAnchorPos, pos)
        set({ runwayAnchorPos: null }, false, 'runwayAnchorClear')
      },

      addRunway: (a, b) => {
        const { draftPlan, activeGroupId } = get()
        if (!draftPlan) return
        const groupId = activeGroupId ?? draftPlan.groups[0]?.id
        if (!groupId) return
        // Derive plausible designators from the runway bearing (a→b heading / 10).
        const heading = (Math.atan2(b[0] - a[0], -(b[1] - a[1])) * 180) / Math.PI
        const norm = (h: number) => ((Math.round(((h % 360) + 360) % 360 / 10) || 36) + '').padStart(2, '0')
        const dA = norm(heading)
        const dB = norm(heading + 180)
        const rwy = mkRunway(groupId, `${dA}/${dB}`, { designator: dA, threshold: a }, { designator: dB, threshold: b })
        set(
          {
            draftPlan: { ...draftPlan, runways: [...(draftPlan.runways ?? []), rwy] },
            dirty: true,
            selectedRunwayId: rwy.id,
            selectedNodeId: null,
            selectedEdgeId: null,
          },
          false,
          'addRunway'
        )
      },

      updateRunway: (id, patch) => {
        const { draftPlan } = get()
        if (!draftPlan) return
        set(
          {
            draftPlan: {
              ...draftPlan,
              runways: (draftPlan.runways ?? []).map((r) => (r.id === id ? { ...r, ...patch } : r)),
            },
            dirty: true,
          },
          false,
          'updateRunway'
        )
      },

      updateRunwayEnd: (id, endIndex, patch) => {
        const { draftPlan } = get()
        if (!draftPlan) return
        set(
          {
            draftPlan: {
              ...draftPlan,
              runways: (draftPlan.runways ?? []).map((r) => {
                if (r.id !== id) return r
                const ends = [r.ends[0], r.ends[1]] as [RunwayEnd, RunwayEnd]
                ends[endIndex] = { ...ends[endIndex], ...patch }
                return { ...r, ends }
              }),
            },
            dirty: true,
          },
          false,
          'updateRunwayEnd'
        )
      },

      moveRunwayEnd: (id, endIndex, threshold) => {
        get().updateRunwayEnd(id, endIndex, { threshold: [threshold[0], threshold[1]] })
      },

      removeRunway: (id) => {
        const { draftPlan } = get()
        if (!draftPlan) return
        set(
          {
            draftPlan: { ...draftPlan, runways: (draftPlan.runways ?? []).filter((r) => r.id !== id) },
            dirty: true,
            selectedRunwayId: null,
          },
          false,
          'removeRunway'
        )
      },

      setActiveRunway: (id) => {
        const { draftPlan } = get()
        if (!draftPlan) return
        set(
          {
            draftPlan: {
              ...draftPlan,
              runways: (draftPlan.runways ?? []).map((r) => ({
                ...r,
                active: { ...r.active, enabled: r.id === id },
              })),
            },
            dirty: true,
          },
          false,
          'setActiveRunway'
        )
      },

      markSaved: () => set({ dirty: false }, false, 'markSaved'),

      resetEditor: () =>
        set(
          {
            mode: 'view',
            selectedNodeId: null,
            selectedEdgeId: null,
            selectedRunwayId: null,
            edgeAnchorNodeId: null,
            runwayAnchorPos: null,
          },
          false,
          'resetEditor'
        ),
    }),
    { name: 'taxi-editor' }
  )
)

/** Selectors */
export const selectSelectedNode = (s: State): TaxiNode | null =>
  s.draftPlan && s.selectedNodeId ? s.draftPlan.nodes.find((n) => n.id === s.selectedNodeId) ?? null : null
export const selectSelectedEdge = (s: State): TaxiEdge | null =>
  s.draftPlan && s.selectedEdgeId ? s.draftPlan.edges.find((e) => e.id === s.selectedEdgeId) ?? null : null
export const selectSelectedRunway = (s: State): TaxiRunway | null =>
  s.draftPlan && s.selectedRunwayId
    ? (s.draftPlan.runways ?? []).find((r) => r.id === s.selectedRunwayId) ?? null
    : null
